import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
    Video, Upload, Play, CheckCircle, Clock, AlertCircle, Trophy,
    Star, TrendingUp, Target, Activity, Eye, BarChart3,
    Loader2, X, FileVideo, RefreshCw, ListChecks, Info,
    Smartphone, Signal, FolderOpen, Plus, Camera
} from 'lucide-react';
import { videoAnalysisApi, portfolioApi, athletesApi } from '../lib/api';
import { toast } from 'sonner';

const VideoAnalysisPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [athleteProfile, setAthleteProfile] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [portfolios, setPortfolios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('portfolio');
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [sportsData, setSportsData] = useState({});
    
    // Portfolio creation
    const [selectedSport, setSelectedSport] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('');
    const [creatingPortfolio, setCreatingPortfolio] = useState(false);
    
    // Active portfolio view
    const [activePortfolio, setActivePortfolio] = useState(null);
    const [uploadingDrill, setUploadingDrill] = useState(null); // index of drill being uploaded
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) setUser(JSON.parse(userData));
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const userData = localStorage.getItem('user');

            const [sportsRes, statsRes, leaderboardRes] = await Promise.allSettled([
                videoAnalysisApi.getSupportedSports(),
                videoAnalysisApi.getStats(),
                videoAnalysisApi.getLeaderboard({ limit: 10 }),
            ]);

            if (sportsRes.status === 'fulfilled') setSportsData(sportsRes.value.data || {});
            if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
            if (leaderboardRes.status === 'fulfilled') setLeaderboard(leaderboardRes.value.data || []);

            try {
                const profileRes = await athletesApi.getMyProfile();
                setAthleteProfile(profileRes.data);
            } catch (e) {}

            if (userData) {
                const u = JSON.parse(userData);
                try {
                    const analysesRes = await videoAnalysisApi.getAthleteAnalyses(u.id);
                    setAnalyses(analysesRes.data || []);
                } catch (e) {}
                try {
                    const portfolioRes = await portfolioApi.getForUser(u.id);
                    setPortfolios(portfolioRes.data.portfolios || []);
                } catch (e) {}
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePortfolio = async () => {
        if (!selectedSport) { toast.error('Please select a sport'); return; }
        if (!athleteProfile) { toast.error('Please create your athlete profile first'); navigate('/profile'); return; }

        setCreatingPortfolio(true);
        try {
            const res = await portfolioApi.create({
                athlete_id: athleteProfile.id || athleteProfile.user_id,
                user_id: user?.id,
                sport: selectedSport,
                position: selectedPosition || null,
            });
            
            toast.success(res.data.existing ? 'Existing portfolio loaded!' : 'Portfolio created!');
            
            // Load the portfolio
            const portfolioRes = await portfolioApi.get(res.data.portfolio_id);
            setActivePortfolio(portfolioRes.data);
            setSelectedSport('');
            setSelectedPosition('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create portfolio');
        } finally {
            setCreatingPortfolio(false);
        }
    };

    const handleDrillUpload = async (drillIndex, file) => {
        if (!activePortfolio) return;
        
        if (file.size > 200 * 1024 * 1024) {
            toast.error('File too large. Max 200MB per drill video');
            return;
        }
        if (!file.type.startsWith('video/')) {
            toast.error('Please select a video file');
            return;
        }

        setUploadingDrill(drillIndex);
        setUploadProgress(10);

        try {
            const formData = new FormData();
            formData.append('drill_index', drillIndex);
            formData.append('video', file);

            setUploadProgress(30);
            const res = await portfolioApi.uploadDrill(activePortfolio.id, formData);
            setUploadProgress(100);
            
            toast.success(`${res.data.drill_name} uploaded! AI analysis started.`);
            
            // Refresh portfolio
            setTimeout(async () => {
                const portfolioRes = await portfolioApi.get(activePortfolio.id);
                setActivePortfolio(portfolioRes.data);
            }, 1000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingDrill(null);
            setUploadProgress(0);
        }
    };

    const openPortfolio = async (portfolio) => {
        try {
            const res = await portfolioApi.get(portfolio.id);
            setActivePortfolio(res.data);
        } catch (error) {
            toast.error('Failed to load portfolio');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'analyzing': return <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />;
            case 'uploaded': return <Clock className="w-5 h-5 text-blue-400" />;
            case 'failed': return <AlertCircle className="w-5 h-5 text-red-400" />;
            default: return <Camera className="w-5 h-5 text-muted-foreground" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'border-green-500/30 bg-green-500/5';
            case 'analyzing': return 'border-yellow-500/30 bg-yellow-500/5';
            case 'uploaded': return 'border-blue-500/30 bg-blue-500/5';
            case 'failed': return 'border-red-500/30 bg-red-500/5';
            default: return 'border-white/10 bg-transparent';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] text-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                    <div className="flex items-center justify-between h-14 md:h-16">
                        <div className="flex items-center gap-2 md:gap-4">
                            <Link to="/dashboard" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-sm md:text-lg tracking-tight uppercase hidden sm:inline">AI Video Analysis</span>
                            </Link>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={loadData} className="border-white/10" data-testid="refresh-btn">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Link to="/dashboard">
                                <Button variant="outline" size="sm" className="border-white/10">Back</Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
                <div className="mb-6">
                    <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight mb-2">
                        AI <span className="text-primary">Talent Scout</span>
                    </h1>
                    <p className="text-muted-foreground text-sm md:text-base">
                        Upload individual drill videos for each category. AI analyzes each drill separately for comprehensive scouting.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {[
                        { id: 'portfolio', icon: FolderOpen, label: 'My Portfolios' },
                        { id: 'new', icon: Plus, label: 'New Portfolio' },
                        { id: 'analyses', icon: Video, label: `All Analyses (${analyses.length})` },
                        { id: 'leaderboard', icon: BarChart3, label: 'Top Talents' },
                    ].map(tab => (
                        <Button key={tab.id} size="sm"
                            variant={activeTab === tab.id ? 'default' : 'outline'}
                            onClick={() => { setActiveTab(tab.id); if (tab.id !== 'portfolio') setActivePortfolio(null); }}
                            className={activeTab !== tab.id ? 'border-white/10' : ''}
                            data-testid={`tab-${tab.id}`}
                        >
                            <tab.icon className="w-4 h-4 mr-2" />{tab.label}
                        </Button>
                    ))}
                </div>

                {/* Active Portfolio View (Drill-by-drill upload) */}
                {activePortfolio && activeTab === 'portfolio' && (
                    <div className="space-y-4">
                        {/* Portfolio Header */}
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4 md:p-6">
                                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div>
                                        <h2 className="text-xl font-bold flex items-center gap-2">
                                            {activePortfolio.sport}
                                            {activePortfolio.position && <span className="text-sm font-normal text-muted-foreground">| {activePortfolio.position}</span>}
                                        </h2>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {activePortfolio.uploaded_count}/{activePortfolio.total_drills} drills uploaded | {activePortfolio.analyzed_count} analyzed
                                        </p>
                                        {activePortfolio.overall_score && (
                                            <p className="text-sm mt-1">
                                                Overall Score: <span className="font-bold text-primary">{activePortfolio.overall_score}/100</span>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" className="border-white/10" onClick={() => setActivePortfolio(null)}>
                                            <X className="w-4 h-4 mr-1" /> Close
                                        </Button>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Progress value={(activePortfolio.uploaded_count / activePortfolio.total_drills) * 100} className="h-2" />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Drill Upload Slots */}
                        <div className="grid gap-3 md:grid-cols-2">
                            {activePortfolio.drill_slots.map((drill, idx) => (
                                <div key={idx} className={`p-4 rounded-lg border transition-colors ${getStatusColor(drill.status)}`} data-testid={`drill-slot-${idx}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                            {getStatusIcon(drill.status)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-medium text-sm truncate">{drill.name}</h4>
                                                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                                                    <span className="text-xs font-bold text-primary">{drill.required_count}</span>
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5">{drill.description}</p>
                                            
                                            {/* Status info */}
                                            {drill.status === 'completed' && drill.analysis_score && (
                                                <p className="text-xs text-green-400 mt-1 font-medium">Score: {drill.analysis_score}/100</p>
                                            )}
                                            {drill.status === 'uploaded' && (
                                                <p className="text-xs text-blue-400 mt-1">Analyzing...</p>
                                            )}
                                            {drill.status === 'failed' && (
                                                <p className="text-xs text-red-400 mt-1">Analysis failed - try re-uploading</p>
                                            )}
                                        </div>
                                        
                                        {/* Upload button / View button */}
                                        <div className="flex-shrink-0">
                                            {drill.status === 'pending' || drill.status === 'failed' ? (
                                                <label className="cursor-pointer">
                                                    <Button size="sm" variant="outline" className="border-white/10 pointer-events-none" asChild>
                                                        <span>
                                                            {uploadingDrill === idx ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <><Upload className="w-3 h-3 mr-1" /> Upload</>
                                                            )}
                                                        </span>
                                                    </Button>
                                                    <input
                                                        type="file"
                                                        accept="video/*"
                                                        className="hidden"
                                                        disabled={uploadingDrill !== null}
                                                        onChange={(e) => {
                                                            if (e.target.files[0]) handleDrillUpload(idx, e.target.files[0]);
                                                        }}
                                                        data-testid={`drill-upload-input-${idx}`}
                                                    />
                                                </label>
                                            ) : drill.status === 'completed' && drill.video_id ? (
                                                <Link to={`/video-analysis/${drill.video_id}`}>
                                                    <Button size="sm" variant="outline" className="border-green-500/30 text-green-400">
                                                        <Eye className="w-3 h-3 mr-1" /> View
                                                    </Button>
                                                </Link>
                                            ) : (
                                                <Button size="sm" variant="ghost" disabled>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Upload progress for this specific drill */}
                                    {uploadingDrill === idx && (
                                        <div className="mt-2">
                                            <Progress value={uploadProgress} className="h-1" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Tip */}
                        <div className="p-4 bg-[#0d2847] border border-white/5 rounded-lg">
                            <p className="text-xs text-muted-foreground">
                                <Info className="w-3 h-3 inline mr-1" />
                                <strong>Tip:</strong> Upload one focused video per drill category. The number shows recommended reps to include. 
                                Multiple focused videos give better AI analysis results than one long compilation.
                            </p>
                        </div>
                    </div>
                )}

                {/* Portfolio List */}
                {activeTab === 'portfolio' && !activePortfolio && (
                    <div className="space-y-4">
                        {portfolios.length === 0 ? (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardContent className="py-12 text-center">
                                    <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground mb-2">No drill portfolios yet</p>
                                    <p className="text-xs text-muted-foreground mb-4">Create a portfolio to start uploading drill-by-drill videos for AI analysis</p>
                                    <Button onClick={() => setActiveTab('new')} data-testid="create-first-portfolio">
                                        <Plus className="w-4 h-4 mr-2" /> Create Portfolio
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            portfolios.map((portfolio) => (
                                <Card key={portfolio.id} className="bg-[#0d2847] border-white/5 hover:border-primary/20 transition-colors cursor-pointer"
                                    onClick={() => openPortfolio(portfolio)} data-testid={`portfolio-card-${portfolio.id}`}>
                                    <CardContent className="p-4 md:p-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                                                <FolderOpen className="w-6 h-6 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold">{portfolio.sport}</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {portfolio.uploaded_count}/{portfolio.total_drills} drills uploaded | {portfolio.analyzed_count} analyzed
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                {portfolio.overall_score ? (
                                                    <div>
                                                        <p className="text-2xl font-bold text-primary">{portfolio.overall_score}</p>
                                                        <p className="text-xs text-muted-foreground">Score</p>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-muted-foreground">
                                                        {Math.round((portfolio.uploaded_count / portfolio.total_drills) * 100)}%
                                                    </div>
                                                )}
                                            </div>
                                            <Progress value={(portfolio.uploaded_count / portfolio.total_drills) * 100} className="w-20 h-2 hidden md:block" />
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                )}

                {/* New Portfolio Tab */}
                {activeTab === 'new' && (
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Plus className="w-5 h-5 text-primary" />
                                    Create Drill Portfolio
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {!athleteProfile && (
                                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                                        <p className="text-yellow-400 text-sm">Create your athlete profile first.</p>
                                        <Link to="/profile"><Button size="sm" className="mt-2">Create Profile</Button></Link>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium mb-2">Sport *</label>
                                    <select value={selectedSport} onChange={(e) => setSelectedSport(e.target.value)}
                                        className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-2 text-sm" data-testid="new-portfolio-sport">
                                        <option value="">Select sport</option>
                                        {Object.keys(sportsData).sort().map(sport => (
                                            <option key={sport} value={sport}>{sport}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedSport && sportsData[selectedSport]?.positions && (
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Position (Optional)</label>
                                        <select value={selectedPosition} onChange={(e) => setSelectedPosition(e.target.value)}
                                            className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-2 text-sm" data-testid="new-portfolio-position">
                                            <option value="">Select position</option>
                                            {sportsData[selectedSport].positions.map(pos => (
                                                <option key={pos} value={pos}>{pos}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Preview drill categories */}
                                {selectedSport && sportsData[selectedSport]?.video_categories && (
                                    <div className="bg-[#0a1628] border border-primary/30 rounded-lg p-4">
                                        <h4 className="font-medium text-primary flex items-center gap-2 mb-3">
                                            <ListChecks className="w-4 h-4" />
                                            {sportsData[selectedSport].video_categories.length} Drill Categories for {selectedSport}
                                        </h4>
                                        <div className="grid gap-2 max-h-64 overflow-y-auto pr-2">
                                            {sportsData[selectedSport].video_categories.map((drill, idx) => (
                                                <div key={idx} className="flex items-center gap-3 p-2 bg-[#0d2847] rounded-lg">
                                                    <div className="w-7 h-7 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <span className="text-xs font-bold text-primary">{drill.count}</span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm">{drill.name}</p>
                                                        <p className="text-xs text-muted-foreground">{drill.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-white/10">
                                            You'll upload one video per category. Each video should include the recommended number of repetitions shown.
                                        </p>
                                    </div>
                                )}

                                <Button className="w-full" onClick={handleCreatePortfolio}
                                    disabled={!selectedSport || !athleteProfile || creatingPortfolio} data-testid="create-portfolio-btn">
                                    {creatingPortfolio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                                    Create Portfolio & Start Uploading
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Info */}
                        <div className="space-y-4">
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg"><Target className="w-5 h-5 text-primary" />How It Works</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {[
                                        { icon: FolderOpen, color: 'text-primary', title: '1. Create Portfolio', desc: 'Choose your sport to see required drill categories' },
                                        { icon: Upload, color: 'text-cyan-400', title: '2. Upload Per Drill', desc: 'One focused video per drill category (from phone)' },
                                        { icon: Activity, color: 'text-green-400', title: '3. AI Analyzes Each', desc: 'Gemini Vision AI scores each drill individually' },
                                        { icon: BarChart3, color: 'text-yellow-400', title: '4. Aggregate Score', desc: 'Overall score + radar chart across all drills' },
                                        { icon: Star, color: 'text-purple-400', title: '5. Get Scouted', desc: 'Scouts see your portfolio with drill-by-drill results' },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <item.icon className={`w-5 h-5 ${item.color} mt-0.5`} />
                                            <div><p className="font-medium text-sm">{item.title}</p><p className="text-xs text-muted-foreground">{item.desc}</p></div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                            {stats && (
                                <Card className="bg-[#0d2847] border-white/5">
                                    <CardContent className="p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="text-center p-3 bg-[#0a1628] rounded-lg">
                                                <p className="text-2xl font-bold text-primary">{stats.total_videos}</p>
                                                <p className="text-xs text-muted-foreground">Videos Analyzed</p>
                                            </div>
                                            <div className="text-center p-3 bg-[#0a1628] rounded-lg">
                                                <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
                                                <p className="text-xs text-muted-foreground">Completed</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                )}

                {/* All Analyses Tab */}
                {activeTab === 'analyses' && (
                    <div className="space-y-4">
                        {analyses.length === 0 ? (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardContent className="py-12 text-center">
                                    <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">No analyses yet</p>
                                </CardContent>
                            </Card>
                        ) : (
                            analyses.map((analysis) => (
                                <Card key={analysis.id} className="bg-[#0d2847] border-white/5 hover:border-primary/20 transition-colors" data-testid={`analysis-card-${analysis.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-[#0a1628] rounded-lg flex items-center justify-center">
                                                <Video className="w-6 h-6 text-muted-foreground" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm">{analysis.sport}</span>
                                                    {analysis.drill_category && <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">{analysis.drill_category}</span>}
                                                    {analysis.analysis_status === 'completed' ? (
                                                        <CheckCircle className="w-4 h-4 text-green-400" />
                                                    ) : analysis.analysis_status === 'processing' ? (
                                                        <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                                                    ) : (
                                                        <Clock className="w-4 h-4 text-blue-400" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground">{new Date(analysis.created_at).toLocaleDateString()}</p>
                                            </div>
                                            {analysis.overall_score && (
                                                <span className="text-lg font-bold text-primary">{analysis.overall_score}</span>
                                            )}
                                            {analysis.analysis_status === 'completed' && (
                                                <Link to={`/video-analysis/${analysis.id}`}>
                                                    <Button size="sm" variant="outline" className="border-white/10">
                                                        <Eye className="w-3 h-3 mr-1" /> View
                                                    </Button>
                                                </Link>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                )}

                {/* Leaderboard Tab */}
                {activeTab === 'leaderboard' && (
                    <Card className="bg-[#0d2847] border-white/5">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-400" />Top Rated Athletes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboard.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No analyses yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {leaderboard.map((item, index) => (
                                        <div key={item.id} onClick={() => navigate(`/video-analysis/${item.id}`)}
                                            className="flex items-center gap-4 p-3 bg-[#0a1628] rounded-lg cursor-pointer hover:bg-[#0a1628]/80 transition-colors"
                                            data-testid={`leaderboard-item-${index}`}>
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                                index === 0 ? 'bg-yellow-500 text-black' : index === 1 ? 'bg-gray-400 text-black' : index === 2 ? 'bg-amber-600 text-black' : 'bg-white/10'
                                            }`}>{index + 1}</div>
                                            <div className="flex-1">
                                                <p className="font-medium">{item.athlete_name}</p>
                                                <p className="text-xs text-muted-foreground">{item.sport}</p>
                                            </div>
                                            <p className="text-xl font-bold text-primary">{item.overall_score}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </main>
        </div>
    );
};

export default VideoAnalysisPage;
